import { useCallback } from 'react';
import { FileIcon, X } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';

export interface PendingFile {
  key: string;
  file: File;
  /** Set once uploaded (chat); the New task form uploads after the task exists. */
  attachmentId: string | null;
  uploading: boolean;
  /** The upload already under way (chat), so sending doesn't start it twice. */
  upload?: Promise<string | null>;
}

function Thumb({ file }: { file: File }) {
  // The preview URL lives exactly as long as the <img>: made when it mounts, freed on removal.
  const attachPreview = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img) return undefined;
      const url = URL.createObjectURL(file);
      img.src = url;
      return () => URL.revokeObjectURL(url);
    },
    [file]
  );
  return file.type.startsWith('image/') ? (
    <img ref={attachPreview} alt="" className="size-12 rounded object-cover" />
  ) : (
    <FileIcon className="size-4 shrink-0 text-muted-foreground" />
  );
}

/** Files waiting to go with a message or a new task: a preview, a spinner while uploading, remove. */
export function PendingFiles({ items, onRemove }: { items: PendingFile[]; onRemove: (item: PendingFile) => void }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <span key={item.key} className="relative inline-flex items-center gap-2 rounded-md border p-1 pr-2 text-xs">
          <Thumb file={item.file} />
          <span className="max-w-[10rem] truncate">{item.file.name}</span>
          {item.uploading ? <Spinner size="xs" label="Uploading" /> : null}
          <button type="button" aria-label={`Remove ${item.file.name}`} onClick={() => onRemove(item)}>
            <X className="size-3" />
          </button>
        </span>
      ))}
    </div>
  );
}
